import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, inject, OnDestroy, OnInit, output, signal, ViewChild} from '@angular/core';
import {CameraService} from './camera.service';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DomSanitizer} from "@angular/platform-browser";
import { getDownloadURL } from 'firebase/storage';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

declare var MediaRecorder: any;

@Component({
    selector: 'app-camera',
    templateUrl: './camera.component.html',
    styleUrls: ['./camera.component.scss'],
    imports: [
      MatIcon,
      MatIconButton,
      NgClass,
      NgIf,
      NgFor,
    ],
    host: {
      '[class.landscape]': 'this.landscape()',
    },
})
export class CameraComponent implements OnInit, AfterViewInit, OnDestroy {

  photoCaptured = output<File>();
  
  progress: number = 0;
  
  @ViewChild('videoplayer', { static: true }) videoPlayer: any;
  
  @ViewChild('canvas', { static: true }) canvas: any;
  
  @ViewChild('circle', {static: true}) circle: any;
  
  @ViewChild('photo', {static: true}) photo: any;
  
  public showVideo = signal(false);
  
  public devices: any[] = [];
  
  public uploading = false;
  
  context: any;
  
  width: number | undefined;
  
  height: number | undefined;
  
  recorder: any;
  
  cameras = signal<MediaDeviceInfo[]>([]);
  
  currentDevice: string | undefined;
  
  stream: MediaStream | undefined;
  
  urls:any = [];
  
  blobs:any = [];
  
  uploads:any = []
  
  cancel = false;
  
  private interval: any;
  
  recording = false;
  
  landscape = signal(false);
  
  recordedChunks:any = [];
  
  microphone = true;

  data = inject(MAT_DIALOG_DATA);

  shutterSound = new Audio('assets/sounds/shutter.mp3');

  videoRecordingStartSound = new Audio('assets/sounds/video-recording-start.wav');
  videoRecordingStopSound = new Audio('assets/sounds/video-recording-stop.wav');

  touchStartHandler: Function = (evt)=>{
    evt.preventDefault();
    this.startVideoProgress()
  };

  touchEndHandler: Function = (evt)=>{
    evt.preventDefault();
    this.stopVideoProgress();
  };

  orientationChangeHandler = () => {
    // Announce the new orientation number
    this.getOrientation();
  };

  constructor(
    private cameraService: CameraService,
    private elementRef: ElementRef,
    private snackbar: MatSnackBar,
    private matDialogRef: MatDialogRef<CameraComponent>,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngAfterViewInit() {
    // Get current geolocation
    this.cdr.markForCheck();
    this.getDevices();
    this.width = this.elementRef.nativeElement.clientWidth;
    this.height = this.elementRef.nativeElement.clientHeight;
    this.videoProgress();
    this.photo.nativeElement.addEventListener('touchstart', this.touchStartHandler);
    this.photo.nativeElement.addEventListener('touchend', this.touchEndHandler)
    window.addEventListener("orientationchange", this.orientationChangeHandler);
    this.getOrientation()
  }

  capture(id?:number) {
    this.showVideo.set(true);

    if (this.landscape()) {
      this.canvas.nativeElement.width = 1920;
      this.canvas.nativeElement.height = 1080;
    } else {
      this.canvas.nativeElement.width = 1080;
      this.canvas.nativeElement.height = 1920;
    }
    this.context = this.canvas.nativeElement.getContext('2d');

    this.context.drawImage(this.videoPlayer.nativeElement, 0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);

    this.progress = 0;
    this.showVideo.set(false);

    this.canvas.nativeElement.toBlob((blob) => {
      const date = new Date().toISOString();
      this.photoCaptured.emit(new File([blob], 'Camera-' + date + '.jpg', { type: 'image/jpeg' }));

      this.shutterSound.play();
      this.snackbar.open('Foto capturada', 'Cerrar', {
        duration: 2000,
        panelClass: 'snackbar-success',
      });
    }, 'image/jpg', 1.0);
  }

  getThumb(image:any){
    if(image){
      const blob = image.blob;
      const thumb = image.downloadUrl;
      return this.sanitizer.bypassSecurityTrustStyle(`url('${blob || thumb}')`);
    }else{
      return null;
    }
  }

  @HostListener('window:resize')
  ngOnInit() {
    this.cdr.markForCheck();
    this.width = this.elementRef.nativeElement.clientWidth;
    this.height = this.elementRef.nativeElement.clientHeight;

    this.matDialogRef.backdropClick().subscribe(() => {
      this.close();
    });
  }

  getDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return null;
    }
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        this.devices = devices;
        this.cameras.set([]);
        devices.forEach((device) => {
          if (device.kind === 'videoinput') {
            this.cameras.update(value => [...value, device]);
          }
        });
        this.currentDevice = this.cameras()[this.cameras().length - 1].deviceId;
        this.loadCamera();
      })
      .catch(function(err) {
        console.log(err.name + ': ' + err.message);
      });
    return true;
  }

  loadCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(function(track) {
        track.stop();
      });
    }
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {

      navigator.mediaDevices.getUserMedia(this.getConstraints())
        .then(stream => {
          this.setupRecorder(stream);
          this.stream = stream;
          this.videoPlayer.nativeElement.volume = 0;
          this.videoPlayer.nativeElement.muted = 0;
          this.videoPlayer.nativeElement.srcObject = stream;
        })
        .catch(err => {
          console.warn('Resolución no soportada. Usando resolución predeterminada.', err);
          navigator.mediaDevices.getUserMedia({ video: {deviceId: this.currentDevice}, audio: this.microphone })
            .then(stream => {
              this.setupRecorder(stream);
              this.stream = stream;
              this.videoPlayer.nativeElement.srcObject = stream;
            });
        });
    }
  }

  setupRecorder(stream:any){
    let mimeType;
    if(MediaRecorder.isTypeSupported('video/webm')){
      mimeType = 'video/webm';
    }else{
      mimeType = 'video/mp4';
    }
    this.recorder = new MediaRecorder(stream,{
      mimeType: mimeType
    });

    this.recorder.addEventListener('dataavailable', (e:any) => {
      if (e.data.size > 0) {
        this.recordedChunks.push(e.data);
      }
    });
    this.recorder.addEventListener('stop', () => {
      if(!this.cancel){
        this.videoRecordingStopSound.play();
        const blob = new Blob(this.recordedChunks);
        this.saveVideo(blob, mimeType)
      }
      this.cancel = false;
    });
  }

  getCameraName() {
    const name = this.devices.find((d) => d.deviceId === this.currentDevice);
    if (name) {
      return name.label;
    }else {
      return 'Cargando';
    }
  }

  getConstraints(){
    let baseConstraints = {
      video: {
        deviceId: this.currentDevice,
        width: { exact: 1920},
        height: { exact: 1080 },
        aspectRatio: { ideal: 1.7777777778 },
        facingMode: {
          ideal: 'environment'
        }
      },
      audio: true
    };
    return baseConstraints
  }

  toggleMic(){
    this.microphone = !this.microphone;
    if(this.stream){
      this.stream.getAudioTracks().forEach(value => {
        value.enabled = this.microphone;
      })
    }
  }

  changeCamera() {
    let index = this.cameras().findIndex((c) => {
      return c.deviceId === this.currentDevice;
    });

    index += 1;
    if (index >= this.cameras().length) {
      index = 0;
    }
    this.currentDevice = this.cameras()[index].deviceId;
    this.loadCamera();
  }

  startRecording(){
    this.videoRecordingStartSound.play();

    this.recordedChunks = [];

    setTimeout(() => {
      this.recorder.start();
    }, 500);

  }

  saveVideo(blob:Blob,mimeType:string) {
    const extension = mimeType.split('/').pop();
    this.snackbar.open('Video añadido', 'Cerrar', {
      duration: 2000,
      panelClass: 'snackbar-success',
    });
    const date = new Date().toISOString();
    const file = new File([blob], `Video-${date}.${extension}`, { type: mimeType }) as any;
    file.video = true;
    this.context = this.canvas.nativeElement.getContext('2d');
    this.context.drawImage(this.videoPlayer.nativeElement, 0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    this.canvas.nativeElement.toBlob(
      (blob) => {
        file.thumb = URL.createObjectURL(blob);
        this.photoCaptured.emit(file);
      },
      'image/jpg',
      1.0
    );
    
  }

  videoProgress() {
    let radius = this.circle.nativeElement.r.baseVal.value;
    let circumference = radius * 2 * Math.PI;
    this.circle.nativeElement.style.strokeDasharray = `${circumference} ${circumference}`;
    this.circle.nativeElement.style.strokeDashoffset = `${circumference}`;
  }

  startVideoProgress(){
    if(this.interval){
      this.cancelVideoProgress()
    }else{
      let seconds = -.5;
      const MAX_SECONDS = 15;
      this.interval = setInterval(()=>{
        seconds += 0.1;
        if(seconds >= MAX_SECONDS){
          seconds = 15;
          this.stopVideoProgress();
        }
        if(seconds >= 1){
          if(!this.recording){
            this.recording = true;
            this.startRecording();

          }
          this.setVideoProgress(seconds / MAX_SECONDS * 100)
        }
      },100)
    }
  }

  cancelVideoProgress(){
    this.cancel = true;
    this.stopVideoProgress();
  }

  stopVideoProgress(){
    if(this.interval){
      if(this.recording){
        this.recording = false;
        this.recorder.stop();
      }else{
        this.capture()
      }
      clearInterval(this.interval);
      this.interval = null;
      this.setVideoProgress(0);
    }else{
      this.recording = false;
      this.cancel = false;
      this.setVideoProgress(0);
    }
  }

  setVideoProgress(percent:number){
    let radius = this.circle.nativeElement.r.baseVal.value;
    let circumference = radius * 2 * Math.PI;
    const offset = circumference - percent / 100 * circumference;
    this.circle.nativeElement.style.strokeDashoffset = offset;
  }

  getOrientation(){
    if(screen.availHeight < screen.availWidth){
      this.landscape.set(true);
    }else{
      this.landscape.set(false);
    }
    this.cdr.detectChanges();
  }

  close() {
    if(!this.uploading){
      if (this.stream) {
        this.stream.getTracks().forEach(function(track) {
          track.stop();
        });
      }
      this.matDialogRef.close({urls: this.urls,blobs: this.blobs});
    }
  }

  openLink(upload:any){
    window.open(upload.url,"_blank")
  }

  ngOnDestroy(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(function(track) {
        track.stop();
      });
    }
    this.photo.nativeElement.removeEventListener('touchstart', this.touchStartHandler);
    this.photo.nativeElement.removeEventListener('touchend', this.touchEndHandler);
    window.removeEventListener("orientationchange", this.orientationChangeHandler);
    if(this.interval){
      clearInterval(this.interval);
    }
  }

}
